import { ComponentType, ReactNode } from 'react';
import type { LucideProps } from 'lucide-react';
import { MoreHorizontal } from 'lucide-react';
import {
	Button,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '@hiring-intelligence/ui';

type LucideIcon = ComponentType<LucideProps>;

export interface QuickAction {
	label: string;
	icon: LucideIcon;
	onClick?: () => void;
	disabled?: boolean;
}

export interface QuickActionMenuItem {
	label: string;
	icon?: ReactNode;
	onClick?: () => void;
}

interface QuickActionsBarProps {
	actions: QuickAction[];
	moreMenu?: QuickActionMenuItem[];
}

/**
 * Row of round icon buttons with labels underneath — the HubSpot "Note / Email /
 * Call / Task / Meeting / More" quick-action strip shown at the top of every
 * record's left rail.
 */
export function QuickActionsBar({ actions, moreMenu }: QuickActionsBarProps) {
	return (
		<TooltipProvider delayDuration={300}>
			<div className="flex items-start justify-around gap-1 rounded-lg border bg-card px-2 py-3">
				{actions.map((a) => {
					const Icon = a.icon;
					return (
						<Tooltip key={a.label}>
							<TooltipTrigger asChild>
								<button
									type="button"
									disabled={a.disabled}
									onClick={a.onClick}
									className="flex flex-col items-center gap-1 group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
								>
									<span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
										<Icon className="h-4 w-4" />
									</span>
									<span className="text-[11px] text-muted-foreground group-hover:text-foreground">{a.label}</span>
								</button>
							</TooltipTrigger>
							<TooltipContent side="bottom">{a.label}</TooltipContent>
						</Tooltip>
					);
				})}
				{moreMenu && moreMenu.length > 0 && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button
								type="button"
								className="flex flex-col items-center gap-1 group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
							>
								<span className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-foreground group-hover:bg-primary/10 group-hover:text-primary">
									<MoreHorizontal className="h-4 w-4" />
								</span>
								<span className="text-[11px] text-muted-foreground group-hover:text-foreground">More</span>
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="center" className="w-52">
							{moreMenu.map((m, i) => (
								<DropdownMenuItem key={i} onClick={m.onClick}>
									{m.icon && <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">{m.icon}</span>}
									{m.label}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
		</TooltipProvider>
	);
}

/** Convenience helper to build the standard activity-logging "More" menu used on all detail pages. */
export function defaultMoreMenu(opts: { onLogSMS?: () => void; onLogLinkedIn?: () => void; onLogWhatsApp?: () => void }): QuickActionMenuItem[] {
	const items: QuickActionMenuItem[] = [];
	if (opts.onLogSMS) items.push({ label: 'Log SMS', onClick: opts.onLogSMS });
	if (opts.onLogLinkedIn) items.push({ label: 'Log a LinkedIn message', onClick: opts.onLogLinkedIn });
	if (opts.onLogWhatsApp) items.push({ label: 'Log a WhatsApp message', onClick: opts.onLogWhatsApp });
	return items;
}
